import React, { useState, useEffect } from 'react';
import { Heart, Users, Award, Target, User, X, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve image URLs - handles both relative and absolute paths
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const About = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/team`);
      setTeamMembers(response.data);
    } catch (error) {
      // Silently fail - team data is optional, fallback to placeholders
    } finally {
      setIsLoading(false);
    }
  };

  const values = [
    {
      icon: Heart,
      title: 'Compassion',
      description: 'We serve with love and empathy, treating every individual with dignity and respect.'
    },
    {
      icon: Award,
      title: 'Clarity & Honesty',
      description: 'We lead with transparency, integrity, and clear communication in all we do.'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'We believe in the power of coming together to create lasting change and support.'
    },
    {
      icon: Target,
      title: 'Impact',
      description: 'We focus on measurable outcomes that truly transform lives and build legacy.'
    }
  ];

  const defaultTeamPlaceholders = [
    { title: 'Executive Director', role: 'Chief Executive Officer', description: 'Leading strategic vision and operations', gradient: 'from-[#0A345E] to-[#007A7A]' },
    { title: 'Program Director', role: 'Head of Programs', description: 'Overseeing all community programs', gradient: 'from-[#007A7A] to-[#85955D]' },
    { title: 'Development Director', role: 'Fundraising & Partnerships', description: 'Building community partnerships', gradient: 'from-[#85955D] to-[#D1C3A2]' },
    { title: 'Operations Director', role: 'Finance & Administration', description: 'Managing day-to-day operations', gradient: 'from-[#0A345E] to-[#85955D]' }
  ];

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#E8E2D0] via-[#F5F2E8] to-[#D1C3A2] py-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-[#0A345E] mb-6">Gateway Inc.</h1>
          <p className="text-xl text-gray-700 leading-relaxed">
            An example of how faith, compassion, and action transforms lives and communities.
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-[#0A345E] mb-8 text-center">Our Story</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Gateway Inc. began over a decade ago as a faith-driven ministry and 501(c)(3) nonprofit with one simple commitment: transformational servant leadership. What started as a ministry and discipleship movement has grown into an anchor for the community for individuals and families across Alabama and beyond.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Over the years, Gateway has continued expanding our reach and impact in the United States and abroad in Africa, and the Caribbean Islands. We don't want to stop there as our heart remains the same. We exist to uplift others, strengthen families, and build communities where everyone has the opportunity to grow and thrive. We do this by deepening our roots in communities and helping those in need stack the odds to win in life.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Gateway Inc. truly is the natural expression of our original assignment - empowering and developing Christ-centered and community-focused leaders across generational lines.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-[#F5F2E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h2 className="text-4xl font-bold text-[#0A345E] mb-6">Our Mission</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                To serve and empower individuals and families in achieving economic well-being through faith-centered and social support services to restore lives, create pathways of stability, and build generational legacy.
              </p>
            </div>
            <div>
              <h2 className="text-4xl font-bold text-[#0A345E] mb-6">Our Vision</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                We envision a vibrant community and county where every individual has access to the resources, support, and opportunities needed to thrive in every area of their life; a future where these transformed lives create ripples of positive change for generations to come.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0A345E] mb-4">Core Values</h2>
            <p className="text-lg text-gray-600">The principles that guide everything we do</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={`value-${value.title}`} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-[#F5F2E8]">
                  <CardHeader>
                    <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br from-[#0A345E] to-[#007A7A] flex items-center justify-center">
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                    <CardTitle className="text-xl text-center text-[#0A345E]">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-center text-sm leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Committed to Transparency */}
      <section className="py-16 bg-[#F5F2E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-[#0A345E] mb-6">Committed to Transparency</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              We maintain the highest standards of financial integrity and accountability. All donations are tax-deductible to the fullest extent allowed by law.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="text-3xl font-bold text-[#007A7A] mb-2">80%</div>
                <p className="text-gray-600">Program Services</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="text-3xl font-bold text-[#007A7A] mb-2">10%</div>
                <p className="text-gray-600">Administrative</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="text-3xl font-bold text-[#007A7A] mb-2">10%</div>
                <p className="text-gray-600">Fundraising</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold text-[#0A345E] mb-4">The Team</h2>
            <p className="text-lg text-gray-600">Dedicated leaders committed to our mission</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 max-w-6xl mx-auto" data-testid="team-members-grid">
            {teamMembers.length > 0 ? (
              teamMembers.map((member, index) => (
                <Card 
                  key={member.id} 
                  className="border-0 shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer hover:scale-[1.02]" 
                  onClick={() => setSelectedMember(member)}
                  data-testid={`team-member-${member.id}`}
                >
                  <CardContent className="p-3 text-center">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-2 rounded-full bg-gradient-to-br from-[#0A345E] to-[#007A7A] flex items-center justify-center overflow-hidden shadow-md">
                      {member.image ? (
                        <img 
                          src={resolveImageUrl(member.image)} 
                          alt={member.name} 
                          className="w-full h-full object-cover"
                          style={{ imageRendering: 'auto' }}
                          fetchpriority="high"
                        />
                      ) : (
                        <User className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                      )}
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-[#0A345E] mb-0.5 line-clamp-1">{member.name}</h3>
                    <p className="text-xs text-gray-600 line-clamp-1">{member.title}</p>
                  </CardContent>
                </Card>
              ))
            ) : (
              defaultTeamPlaceholders.map((placeholder, index) => (
                <Card key={`placeholder-${placeholder.title}`} className="border-0 shadow-md hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-3 text-center">
                    <div className={`w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-2 rounded-full bg-gradient-to-br ${placeholder.gradient} flex items-center justify-center shadow-md`}>
                      <Users className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-[#0A345E] mb-0.5">{placeholder.title}</h3>
                    <p className="text-xs text-gray-600">{placeholder.role}</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Team Member Modal - Fully Responsive */}
      {selectedMember && (
        <div 
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedMember(null)}
        >
          <Card 
            className="w-full max-w-md bg-white shadow-2xl relative max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button - Always Visible */}
            <button 
              onClick={() => setSelectedMember(null)}
              className="absolute top-3 right-3 p-2 rounded-full bg-white/90 hover:bg-white shadow-md transition-colors z-20"
              data-testid="close-member-modal"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
            
            {/* Header with Image - Fixed Height */}
            <div className="bg-gradient-to-br from-[#0A345E] to-[#007A7A] p-5 text-center flex-shrink-0">
              <div className="w-24 h-24 mx-auto rounded-full bg-white/20 flex items-center justify-center overflow-hidden border-3 border-white/30 shadow-lg">
                {selectedMember.image ? (
                  <img 
                    src={resolveImageUrl(selectedMember.image)} 
                    alt={selectedMember.name} 
                    className="w-full h-full object-cover"
                    style={{ imageRendering: 'auto' }}
                    fetchpriority="high"
                  />
                ) : (
                  <User className="w-10 h-10 text-white" />
                )}
              </div>
              <h3 className="text-xl font-bold text-white mt-3 mb-0.5">{selectedMember.name}</h3>
              <p className="text-white/90 text-sm">{selectedMember.title}</p>
            </div>
            
            {/* Scrollable Content */}
            <CardContent className="p-4 overflow-y-auto flex-1">
              {selectedMember.bio && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-1.5">About</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{selectedMember.bio}</p>
                </div>
              )}
              
              {selectedMember.email && (
                <div className="border-t pt-3">
                  <h4 className="text-sm font-semibold text-gray-700 mb-1.5">Contact</h4>
                  <a 
                    href={`mailto:${selectedMember.email}`}
                    className="flex items-center gap-2 text-sm text-[#007A7A] hover:text-[#0A345E] transition-colors"
                  >
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span className="break-all">{selectedMember.email}</span>
                  </a>
                </div>
              )}
              
              {!selectedMember.bio && !selectedMember.email && (
                <p className="text-gray-500 text-center text-sm italic">Contact information coming soon.</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default About;
